<script setup>
import { computed } from 'vue';
import UiCard from './UiCard.vue';

// Horizontal bar list: one row per label (e.g. grade level), one thin bar per
// series (e.g. enrolled, or at-risk + LARDO), scaled to the largest value.
const props = defineProps({
  title: { type: String, required: true },
  description: { type: String, default: '' },
  rows: { type: Array, required: true }, // [{ label, ...values }]
  series: { type: Array, required: true }, // [{ key, label, color }]
  loading: { type: Boolean, default: false },
  emptyText: { type: String, default: 'No data for this school year yet.' },
});

const max = computed(() =>
  Math.max(1, ...props.rows.flatMap((r) => props.series.map((s) => Number(r[s.key]) || 0))),
);
const isEmpty = computed(() =>
  props.rows.every((r) => props.series.every((s) => !Number(r[s.key]))),
);
const width = (value) => `${((Number(value) || 0) / max.value) * 100}%`;
</script>

<template>
  <UiCard :title="title" :description="description">
    <template v-if="series.length > 1" #actions>
      <div class="legend">
        <span v-for="s in series" :key="s.key" class="legend__item">
          <i :style="{ background: s.color }" />{{ s.label }}
        </span>
      </div>
    </template>

    <div v-if="loading" class="chart">
      <div v-for="n in 4" :key="n" class="chart__skeleton" />
    </div>
    <p v-else-if="isEmpty" class="chart__empty">{{ emptyText }}</p>
    <div v-else class="chart">
      <div v-for="row in rows" :key="row.label" class="chart__row">
        <span class="chart__label">{{ row.label }}</span>
        <div class="chart__bars">
          <div v-for="s in series" :key="s.key" class="chart__track">
            <span
              class="chart__bar"
              :style="{ width: width(row[s.key]), background: s.color }"
              :title="`${s.label}: ${row[s.key] || 0}`"
            />
            <span class="chart__value">{{ row[s.key] || 0 }}</span>
          </div>
        </div>
      </div>
    </div>
  </UiCard>
</template>

<style scoped>
.legend {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 12px;
  color: var(--muted-foreground);
}
.legend__item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.legend__item i {
  width: 8px;
  height: 8px;
  border-radius: 2px;
}

.chart {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.chart__row {
  display: grid;
  grid-template-columns: 72px 1fr;
  align-items: center;
  gap: 12px;
}
.chart__label {
  font-size: 13px;
  color: var(--muted-foreground);
  white-space: nowrap;
}
.chart__bars {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.chart__track {
  display: flex;
  align-items: center;
  gap: 8px;
}
.chart__bar {
  height: 10px;
  min-width: 2px;
  border-radius: 3px;
  transition: width 0.4s ease;
}
.chart__value {
  font-size: 12px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  color: var(--foreground);
}
.chart__skeleton {
  height: 14px;
  border-radius: 4px;
  background: var(--accent);
}
.chart__empty {
  margin: 0;
  padding: 24px 0;
  font-size: 14px;
  text-align: center;
  color: var(--muted-foreground);
}
</style>
